import { deepmerge } from '@mui/utils';

import {
  CreateFieldGlassCandidateIntroductionInput,
  SaveFieldGlassCandidateIntroductionInput,
  FieldGlassCandidateIntroduction,
  Job,
  JobMatch,
  TalentDocument,
} from '@libs/graphql-types';

import {
  generateDefaultSecurityId,
  generateBayerSecurityId,
  generateWtwSecurityId,
  generateDukeEnergySecurityId,
  getSecurityIdData,
  isBayerJob,
  isWtwJob,
  isDukeEnergyJob,
} from './security-id-utils';
import { ApplicationFormState } from './types';

const updateArrayWithFormValues = (
  originalArray: any[],
  formValues: Record<string, string>,
) => {
  return originalArray
    .map((item) => ({
      question: item.question || item.recordId || item.name,
      answer: formValues[item.recordId] || '',
    }))
    .filter((i) => !!i.answer);
};

interface MapFormToFieldGlassInputProps {
  formData: ApplicationFormState;
  jobData: Job;
  jobMatch: JobMatch;
  currentUserId: string;
}

const formatDateToServer = (date?: Date | null) => {
  if (!date) {
    return null;
  }

  return date.toISOString().replace('T', ' ').split('.')[0];
};

const convertYesNoToBoolean = (value: string | boolean): boolean => {
  return value === 'true' || value === true;
};

/**
 * Generates security ID based on job type and form data
 * This helper function avoids code duplication across multiple mappers
 */
const generateSecurityIdFromFormData = (
  jobData: Job,
  formData: ApplicationFormState,
): string => {
  if (isBayerJob(jobData)) {
    return generateBayerSecurityId(
      formData.basicInfo.firstName,
      formData.basicInfo.lastName,
      formData.details.monthOfBirth,
      formData.details.dayOfBirth,
    );
  }

  if (isWtwJob(jobData)) {
    return generateWtwSecurityId(
      formData.basicInfo.firstName,
      formData.basicInfo.lastName,
      formData.details.monthOfBirth,
      formData.details.dayOfBirth,
      formData.details.ssnOrPassportLast3Digits,
    );
  }

  if (isDukeEnergyJob(jobData)) {
    return generateDukeEnergySecurityId(
      formData.details.monthOfBirth,
      formData.details.dayOfBirth,
      formData.details.ssnLast4Digits,
    );
  }

  return generateDefaultSecurityId(
    formData.basicInfo.firstName,
    formData.details.monthOfBirth,
    formData.details.dayOfBirth,
    formData.details.ssnLast4Digits,
  );
};

export const mapFormToFieldGlassInput = ({
  formData,
  jobData,
  jobMatch,
  currentUserId,
}: MapFormToFieldGlassInputProps): CreateFieldGlassCandidateIntroductionInput => {
  const jobId = jobData?.id;
  const questions = JSON.parse(jobData?.striive_job?.questions || '[]');
  const qualifications = JSON.parse(
    jobData?.striive_job?.qualifications || '[]',
  );
  const siteCode = formData.details.siteCode.value as string;
  const locationCode =
    (formData.questions['Location Code'] as string) || siteCode;

  const cvAttachmentHash = formData.resume.newCvUpload?.hash;
  // const candidateCvHash = formData.resume.filename;
  const cvHash = cvAttachmentHash; // || candidateCvHash;

  // Generate security ID from separate fields if not already present
  const securityId =
    formData.details.securityId ||
    generateSecurityIdFromFormData(jobData, formData);

  return {
    job_id: jobId,
    first_name: formData.basicInfo.firstName,
    last_name: formData.basicInfo.lastName,
    date_available: formatDateToServer(formData.details.availableDate),
    email: jobMatch?.talent?.email_abac?.value || '',

    display_candidate_workforce_record_to_the_buyer: convertYesNoToBoolean(
      formData.details.displayWorkforceRecord,
    ),
    submitted_to_other_job_postings: convertYesNoToBoolean(
      formData.details.submittedToOtherJobs,
    ),
    submitted_by_user_id: currentUserId,
    job_posting_id: formData.basicInfo.jobPostingId,
    external_job_seeker_id: securityId,
    worker_pay_type: formData.details.workerPayType,

    ...(cvHash ? { candidate_cv_hash: cvHash } : {}),

    qualifications: JSON.stringify(
      updateArrayWithFormValues(qualifications, formData.qualifications),
    ),
    answers: JSON.stringify(
      updateArrayWithFormValues(questions, formData.questions),
    ),
    security_id: securityId,

    // TODO: get the fields
    // supplier_code: 'GGDX',
    site_code: siteCode as string,
    location_code: locationCode as string,
    // NOTE: We need to get this data from the Security ID. The Security ID is a combination of SSN and date of birth
    // date_of_birth: '',
    // date_of_birth: formatDateToServer(formData.basicInfo.dateOfBirth),

    ...(formData.rateInfo.standardRate && {
      standard_pay_rate: formData.rateInfo.standardRate,
    }),
    ...(formData.rateInfo.standardBillRate && {
      standard_bill_rate: formData.rateInfo.standardBillRate,
    }),

    // comments: formData.additionalNotes,
    // Additional fields can be mapped as needed
    // additional_invoice_currency: null,
    // calculate_rate_using: null,
    // date_of_birth: null,
    // external_job_seeker_id: null,
    // job_seeker_code: null,
    // number_of_tiers: null,
    // payment_currency: null,
    // primary_location_code: null,
    // rate_change_matrix_start_date: null,
    // register_on_behalf_of_worker: null,
    // remit_to_address_code: null,
    // select_rates: null,
    // site_code: null,
    // social_security_number_suffix: null,
    // supplier_code: null,
    // supplier_email: null,
    // time_zone: null,
    // unique_id: null,
    // worker_country_of_origin: null,
  };
};

export const mapFormToFieldGlassSaveInput = ({
  formData,
  jobData,
  jobMatch,
  currentUserId,
}: MapFormToFieldGlassInputProps): SaveFieldGlassCandidateIntroductionInput => {
  const questions = JSON.parse(jobData?.striive_job?.questions || '[]');
  const qualifications = JSON.parse(
    jobData?.striive_job?.qualifications || '[]',
  );
  const siteCode = formData.details.siteCode.value as string;
  const locationCode =
    // # NOTE: Location code is the same as site code for now
    formData.questions['Location Code'] || siteCode;

  const cvAttachmentHash = formData.resume.newCvUpload?.hash;
  const candidateCvHash = formData.resume.filename;
  const cvHash = cvAttachmentHash || candidateCvHash;

  // Generate security ID from separate fields if not already present
  const securityId =
    formData.details.securityId ||
    generateSecurityIdFromFormData(jobData, formData);

  return {
    first_name: formData.basicInfo.firstName,
    last_name: formData.basicInfo.lastName,
    date_available: formatDateToServer(formData.details.availableDate),
    email: jobMatch?.talent?.email_abac?.value || '',

    display_candidate_workforce_record_to_the_buyer: convertYesNoToBoolean(
      convertYesNoToBoolean(formData.details.displayWorkforceRecord),
    ),
    submitted_to_other_job_postings: convertYesNoToBoolean(
      formData.details.submittedToOtherJobs,
    ),
    submitted_by_user_id: currentUserId,
    job_posting_id: formData.basicInfo.jobPostingId,
    external_job_seeker_id: securityId,
    worker_pay_type: formData.details.workerPayType,

    ...(cvHash ? { candidate_cv_hash: cvHash } : {}),

    qualifications: JSON.stringify(
      updateArrayWithFormValues(qualifications, formData.qualifications),
    ),
    answers: JSON.stringify(
      updateArrayWithFormValues(questions, formData.questions),
    ),
    security_id: securityId,

    // TODO: get the fields
    // supplier_code: 'GGDX',
    site_code: siteCode,
    location_code: locationCode,
    // NOTE: We need to get this data from the Security ID. The Security ID is a combination of SSN and date of birth
    // date_of_birth: '',
    // date_of_birth: formatDateToServer(formData.basicInfo.dateOfBirth),

    ...(formData.rateInfo.standardRate && {
      standard_pay_rate: formData.rateInfo.standardRate,
    }),
    ...(formData.rateInfo.standardBillRate && {
      standard_bill_rate: formData.rateInfo.standardBillRate,
    }),

    // comments: formData.additionalNotes,
    // Additional fields can be mapped as needed
    // additional_invoice_currency: null,
    // calculate_rate_using: null,
    // date_of_birth: null,
    // external_job_seeker_id: null,
    // job_seeker_code: null,
    // number_of_tiers: null,
    // payment_currency: null,
    // primary_location_code: null,
    // rate_change_matrix_start_date: null,
    // register_on_behalf_of_worker: null,
    // remit_to_address_code: null,
    // select_rates: null,
    // site_code: null,
    // social_security_number_suffix: null,
    // supplier_code: null,
    // supplier_email: null,
    // time_zone: null,
    // unique_id: null,
    // worker_country_of_origin: null,
  };
};

export const mapQuestionsToState = (
  questions: any[],
): Record<string, string> => {
  return questions.reduce((acc, question) => {
    acc[question.recordId] = question.value || '';
    return acc;
  }, {} as Record<string, string>);
};

export const mapQualificationsToState = (
  qualifications: any[],
): Record<string, string> => {
  return qualifications.reduce((acc, qualification) => {
    acc[qualification.recordId] = qualification.value || '';
    return acc;
  }, {} as Record<string, string>);
};

// DeepPartial utility type
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export const DEFAULT_APPLICATION_FORM_STATE: ApplicationFormState = {
  basicInfo: {
    firstName: '',
    lastName: '',
    dateOfBirth: null,
    email: '',
    jobName: '',
    jobPostingId: '',
    visibleToAllBuyers: true,
    visibleToSelectedBuyers: false,
    availableToAllBuyers: true,
    availableToSelectedBuyers: false,
  },
  resume: {
    file: null,
    filename: '',
  },
  details: {
    securityId: '',
    confirmSecurityId: '',
    monthOfBirth: '' as any,
    dayOfBirth: '' as any,
    ssnLast4Digits: '',
    ssnOrPassportLast3Digits: '',
    availableDate: null,
    submittedToOtherJobs: false,
    workerPayType: '',
    displayWorkforceRecord: true,
    education: '',
    county: '',
    previouslyWorked: '',
    locationCode: '' as any,
    siteCode: '' as any,
    recruitingSource: '',
    veteran: '',
  },
  additionalInfo: {
    languagesSpoken: '',
    microsoftWordScore: null,
    microsoftExcelScore: null,
    typingScoreWPM: null,
    dataEntryAlpha: null,
    callCenterReferenceManualScore: null,
    callCenterDataEntryScore: null,
    callCenterListeningSkillsScore: null,
    customerServiceMindsetSurveyScore: null,
    callCenterSpanishEnglishBilingualScore: null,
    callCenterTelephoneEtiquetteScore: null,
    spanishEnglishBilingualScore: null,
    spanishCallCenterEtiquetteScore: null,
    analyticalSkillsScore: null,
    readingComprehension: '',
    basicComputerLiteracy: '',
    callCenterScenarios: '',
    microsoftOffice: '',
    msInternetExplorer: '',
    businessEtiquette: '',
    followingWritingInstructions: '',
    hireSmartAssessmentSurvey: '',
    additionalInformation: '',
  },
  rateInfo: {
    standardRate: null,
    standardBillRate: null,
  },
  questions: {},
  qualifications: {},
  additionalNotes: '',
};

export const mapJobToFormState = (
  job?: Job,
  jobMatch?: JobMatch,
): DeepPartial<ApplicationFormState> => {
  if (!job) return {};
  const questions = JSON.parse(job?.striive_job?.questions || '[]');
  const qualifications = JSON.parse(job?.striive_job?.qualifications || '[]');

  // Get the first CV document from talent's documents
  const talentDocuments = jobMatch?.talent?.documents || [];
  const defaultResume =
    talentDocuments.find((doc) => doc?.type === 'cv') || talentDocuments[0]; // Fallback to first document if no CV found

  return {
    basicInfo: {
      ...(jobMatch?.talent?.first_name_abac?.value && {
        firstName: jobMatch?.talent?.first_name_abac?.value,
      }),
      ...(jobMatch?.talent?.last_name_abac?.value && {
        lastName: jobMatch?.talent?.last_name_abac?.value,
      }),
      ...(jobMatch?.talent?.email_abac?.value && {
        email: jobMatch?.talent?.email_abac?.value,
      }),
      ...(job?.striive_job?.record_id && {
        jobPostingId: job?.striive_job?.record_id,
      }),
      ...(job?.name && {
        jobName: job?.name,
      }),
    },
    // Include resume if talent has documents
    ...(defaultResume?.hash && {
      resume: {
        filename: defaultResume.hash,
      },
    }),
    questions: mapQuestionsToState(questions),
    qualifications: mapQualificationsToState(qualifications),
  };
};

export const mapCandidateIntroductionToFormState = ({
  job,
  candidateIntroduction,
}: {
  job?: Job;
  candidateIntroduction?: FieldGlassCandidateIntroduction;
}): DeepPartial<ApplicationFormState> => {
  if (!candidateIntroduction) return {};

  // Parse answers and qualifications if present
  let questions: Record<string, string> = {};
  let qualifications: Record<string, string> = {};
  try {
    if (candidateIntroduction.answers) {
      const parsed =
        typeof candidateIntroduction.answers === 'string'
          ? JSON.parse(candidateIntroduction.answers)
          : candidateIntroduction.answers;
      questions = Array.isArray(parsed)
        ? parsed.reduce((acc, q) => {
            if (q.question && q.answer !== undefined)
              acc[q.question] = q.answer;
            return acc;
          }, {} as Record<string, string>)
        : {};
    }
    if (candidateIntroduction.qualifications) {
      const parsed =
        typeof candidateIntroduction.qualifications === 'string'
          ? JSON.parse(candidateIntroduction.qualifications)
          : candidateIntroduction.qualifications;
      qualifications = Array.isArray(parsed)
        ? parsed.reduce((acc, q) => {
            if (q.question && q.answer !== undefined)
              acc[q.question] = q.answer;
            return acc;
          }, {} as Record<string, string>)
        : {};
    }
  } catch (e) {
    // ignore parse errors
  }

  const resumeFile = candidateIntroduction.candidate_cv_hash;

  return {
    rateInfo: {
      standardRate: candidateIntroduction.standard_pay_rate,
      standardBillRate: candidateIntroduction.standard_bill_rate,
    },
    introductionId: candidateIntroduction.id,
    resume: {
      ...(resumeFile && { filename: resumeFile }),
      newCvUpload: {
        hash: candidateIntroduction.cv_attachment?.hash || '',
        title: candidateIntroduction.cv_attachment?.title || '',
      } as TalentDocument,
    },
    basicInfo: {
      ...(candidateIntroduction.first_name && {
        firstName: candidateIntroduction.first_name,
      }),
      ...(candidateIntroduction.last_name && {
        lastName: candidateIntroduction.last_name,
      }),
      ...(candidateIntroduction.email && {
        email: candidateIntroduction.email,
      }),
      ...(candidateIntroduction.job_posting_id && {
        jobPostingId: candidateIntroduction.job_posting_id,
      }),
      // ...(candidateIntroduction.date_of_birth && {
      //   dateOfBirth: new Date(candidateIntroduction.date_of_birth),
      // }),
    },
    details: {
      ...(candidateIntroduction.site_code && {
        siteCode: {
          value: candidateIntroduction.site_code,
          text: candidateIntroduction.site_code,
        },

        locationCode: {
          value: candidateIntroduction.site_code,
          text: candidateIntroduction.site_code,
        },
      }),
      ...(candidateIntroduction.security_id && {
        securityId: candidateIntroduction.security_id,
      }),
      ...(candidateIntroduction.security_id && {
        confirmSecurityId: candidateIntroduction.security_id,
      }),
      // Parse security ID into separate components
      ...(candidateIntroduction.security_id &&
        (() => {
          const parsed = !!job
            ? getSecurityIdData({
                job,
                introduction: candidateIntroduction,
              })
            : ({} as any);
          return {
            monthOfBirth: parsed.monthOfBirth,
            dayOfBirth: parsed.dayOfBirth,
            ssnLast4Digits: parsed.ssnLast4Digits,
            ssnOrPassportLast3Digits: parsed.ssnOrPassportLast3Digits,
          };
        })()),
      ...(candidateIntroduction.date_available && {
        availableDate: new Date(candidateIntroduction.date_available),
      }),
      ...(typeof candidateIntroduction.submitted_to_other_job_postings ===
        'boolean' && {
        submittedToOtherJobs:
          candidateIntroduction.submitted_to_other_job_postings,
      }),
      ...(candidateIntroduction.worker_pay_type && {
        workerPayType: candidateIntroduction.worker_pay_type,
      }),
      ...(typeof candidateIntroduction.display_candidate_workforce_record_to_the_buyer ===
        'boolean' && {
        displayWorkforceRecord:
          candidateIntroduction.display_candidate_workforce_record_to_the_buyer,
      }),
    },
    ...(Object.keys(questions).length > 0 && { questions }),
    ...(Object.keys(qualifications).length > 0 && { qualifications }),
    ...(candidateIntroduction.comments && {
      additionalNotes: candidateIntroduction.comments,
    }),
  };
};

export const mapApplicationFormDataToInitialValues = ({
  job,
  candidateIntroduction,
  matchId,
}: {
  job?: Job;
  candidateIntroduction?: FieldGlassCandidateIntroduction;
  matchId: string;
}) => {
  const jobMatch = job?.matches?.find((m) => m?.id === matchId);
  // Merge in order: default, mapped job, mapped candidateIntroduction
  const merged = deepmerge(
    deepmerge(DEFAULT_APPLICATION_FORM_STATE, mapJobToFormState(job, jobMatch)),
    mapCandidateIntroductionToFormState({ job, candidateIntroduction }),
  );

  return {
    initialValues: merged as ApplicationFormState,
  };
};
